// TeachersWorkReport.js (FULL, sa brand logom i blagim brandingom)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';
import * as XLSX from 'xlsx';

// ---------- Helpers ----------
const toFixed = (v, n = 2) => {
  const num = Number(v);
  return Number.isFinite(num) ? num.toFixed(n) : (0).toFixed(n);
};
const fmtMoney = (v) => toFixed(v, 2);
const kmToMiles = (km) => {
  const n = Number(km);
  return Number.isFinite(n) ? n * 0.621371 : null;
};
const safe = (v, fallback = '-') => (v === null || v === undefined || v === '' ? fallback : v);
const toISODate = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

function groupBy(array, keyFn) {
  const out = {};
  for (const item of array || []) {
    const key = keyFn(item);
    (out[key] ||= []).push(item);
  }
  return out;
}

export default function TeachersWorkReport() {
  const [teachers, setTeachers] = useState([]);
  const [filters, setFilters] = useState({
    teacher_id: '',
    student_id: '',
    date_from: '',
    date_to: ''
  });

  const [visits, setVisits] = useState([]);
  const [dailyTotals, setDailyTotals] = useState([]);
  const [totals, setTotals] = useState({
    obj: { hours: 0, distance_km: 0, travel: 0, service: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 },
    subj:{ hours: 0, distance_km: 0, travel: 0, service: 0, extra: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 }
  });

  const [teacherName, setTeacherName] = useState('');
  const [teacherAddress, setTeacherAddress] = useState('');
  const [serverTimeStr, setServerTimeStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // brand info
  const [brandName, setBrandName] = useState('EarlyCare');
  const [brandLogoUrl, setBrandLogoUrl] = useState('/VisitOMS_logo.png');

  const reportRef = useRef();

  // --- Load selects + server time + brand ---
  useEffect(() => {
    (async () => {
      try {
        const [tRes, timeRes, brandRes, logoRes] = await Promise.all([
          API.get('/teachers'),
          API.get('/server-time').catch(() => ({ data: {} })),
          API.get('/brand-settings').catch(() => ({ data: {} })),
          API.get('/brand-logo').catch(() => ({ data: {} })),
        ]);
        setTeachers(Array.isArray(tRes.data) ? tRes.data : []);
        setServerTimeStr(timeRes?.data?.serverTime || '');

        const name = (brandRes.data?.brand_name || '').trim();
        if (name) setBrandName(name);

        const hasLogo =
          !!(logoRes.data?.logo_url ||
             logoRes.data?.brand_logo_path ||
             logoRes.data?.has_logo);
        if (hasLogo) {
          setBrandLogoUrl('/api/brand-logo/image');
        } else {
          setBrandLogoUrl('/VisitOMS_logo.png');
        }
      } catch (e) {
        console.warn('Init load failed:', e);
      }
    })();
  }, []);

  // --- Default: poslednjih 30 dana ---
  useEffect(() => {
    const now = serverTimeStr ? new Date(serverTimeStr) : new Date();
    if (Number.isNaN(now.getTime())) return;
    setFilters((f) => {
      if (f.date_from && f.date_to) return f;
      const end = toISODate(now);
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      const start = toISODate(startDate);
      return { ...f, date_from: start, date_to: end };
    });
  }, [serverTimeStr]);

  const fetchReport = async () => {
    if (!filters.teacher_id) {
      alert('Please choose a teacher.');
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const res = await API.post('/reports/teachers-work', filters);
      const v = Array.isArray(res.data?.visits) ? res.data.visits : [];
      const dt = Array.isArray(res.data?.dailyTotals) ? res.data.dailyTotals : [];
      const tt = res.data?.totals || {
        obj: { hours: 0, distance_km: 0, travel: 0, service: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 },
        subj:{ hours: 0, distance_km: 0, travel: 0, service: 0, extra: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 }
      };

      setVisits(v);
      setDailyTotals(dt);
      setTotals(tt);

      if (v.length > 0) {
        setTeacherName(v[0].teacher || '');
        setTeacherAddress(v[0].teacherAddress || '');
      } else {
        setTeacherName('');
        setTeacherAddress('');
      }
    } catch (e) {
      console.error(e);
      setLoadError('Failed to fetch report data.');
      setVisits([]);
      setDailyTotals([]);
      setTotals({
        obj: { hours: 0, distance_km: 0, travel: 0, service: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 },
        subj:{ hours: 0, distance_km: 0, travel: 0, service: 0, extra: 0, total: 0, route_minutes: 0, route_time_cost: 0, paid_break_minutes: 0, paid_break_cost: 0 }
      });
      setTeacherName('');
      setTeacherAddress('');
    } finally {
      setLoading(false);
    }
  };

  // --- Grouping (defensive) ---
  const groupedVisits = useMemo(() => {
    return groupBy(visits, (v) => {
      const d = v?.date || (typeof v?.scheduled_at === 'string' ? v.scheduled_at.slice(0, 10) : '');
      return String(d || 'Unknown date');
    });
  }, [visits]);

  const dayKeys = useMemo(() => Object.keys(groupedVisits).sort(), [groupedVisits]);

  // Visits (excluding No-show)
  const nonNoShowCount = useMemo(() => {
    return (visits || []).filter(v => !v?.isNoShow).length;
  }, [visits]);

  const handleExportExcel = () => {
    if (!filters.teacher_id) {
      alert('Please choose a teacher.');
      return;
    }
    if (!visits.length) {
      alert('No data to export for the selected period/teacher.');
      return;
    }
    try {
      const sheetData = [];
      const studentName = 'All';

      sheetData.push([
        'Period', filters.date_from || '—', 'to', filters.date_to || '—',
        'Teacher', teacherName || 'All',
        'Address', teacherAddress || '-',
        'Student', studentName
      ]);
      sheetData.push([]);

      for (const date of dayKeys) {
        const dayVisits = groupedVisits[date] || [];
        const subtotal = dailyTotals.find((dt) => dt.date === date);
        const dayBonus = dayVisits.reduce((s, r) => s + (Number(r?.bonus) || 0), 0);
        const dayObjRouteMin  = subtotal?.obj?.route_minutes ?? dayVisits.reduce((s, r) => s + (Number(r?.obj?.route_minutes) || 0), 0);
        const daySubjRouteMin = subtotal?.subj?.route_minutes ?? dayVisits.reduce((s, r) => s + (Number(r?.subj?.route_minutes) || 0), 0);

        sheetData.push([`Date: ${date}`]);
        if (subtotal) {
          sheetData.push([
            'Paid breaks (count)',
            subtotal?.paid_breaks_count ?? 0,
            'Obj paid break min / $',
            subtotal?.obj?.paid_break_minutes ?? 0,
            fmtMoney(subtotal?.obj?.paid_break_cost ?? 0),
            'Subj paid break min / $',
            subtotal?.subj?.paid_break_minutes ?? 0,
            fmtMoney(subtotal?.subj?.paid_break_cost ?? 0)
          ]);
        }
        sheetData.push([
          'Student', 'Time',
          'Hours (obj)', 'Hours (subj)',
          'Distance km (obj)', 'Distance km (subj)',
          'Route time min (obj)', 'Route time min (subj)',
          'Service $ (obj)', 'Service $ (subj)',
          'Travel $ (obj)',  'Travel $ (subj)',
          'Extra $ (subj)',
          'Bonus $',
          'Total $ (obj)', 'Total $ (subj)',
          'Paid break min'
        ]);

        for (const row of dayVisits) {
          const obj = row?.obj || {};
          const subj = row?.subj || {};
          sheetData.push([
            safe(row?.student),
            safe(row?.timeSlot, ''),
            toFixed(obj.hours),
            toFixed(subj.hours),
            toFixed(obj.distance_km),
            toFixed(subj.distance_km),
            toFixed(obj.route_minutes),
            toFixed(subj.route_minutes),
            fmtMoney(obj.service_cost),
            fmtMoney(subj.service_cost),
            fmtMoney(obj.travel_cost),
            fmtMoney(subj.travel_cost),
            fmtMoney(subj.extra_cost),
            fmtMoney(row?.bonus || 0),
            fmtMoney(obj.total_cost),
            fmtMoney(subj.total_cost),
            row?.paid_break_min || 0
          ]);
        }

        if (subtotal) {
          sheetData.push([
            'Subtotal', '',
            toFixed(subtotal?.obj?.hours),  toFixed(subtotal?.subj?.hours),
            toFixed(subtotal?.obj?.distance_km), toFixed(subtotal?.subj?.distance_km),
            toFixed(dayObjRouteMin), toFixed(daySubjRouteMin),
            fmtMoney(subtotal?.obj?.service), fmtMoney(subtotal?.subj?.service),
            fmtMoney(subtotal?.obj?.travel),  fmtMoney(subtotal?.subj?.travel),
            fmtMoney(subtotal?.subj?.extra),
            fmtMoney(dayBonus),
            fmtMoney(subtotal?.obj?.total),  fmtMoney(subtotal?.subj?.total),
            subtotal?.obj?.paid_break_minutes ?? 0
          ]);
        }
        sheetData.push([]);
      }

      sheetData.push([
        'TOTAL', '',
        toFixed(totals?.obj?.hours),   toFixed(totals?.subj?.hours),
        toFixed(totals?.obj?.distance_km), toFixed(totals?.subj?.distance_km),
        toFixed(totals?.obj?.route_minutes), toFixed(totals?.subj?.route_minutes),
        fmtMoney(totals?.obj?.service), fmtMoney(totals?.subj?.service),
        fmtMoney(totals?.obj?.travel),  fmtMoney(totals?.subj?.travel),
        fmtMoney(totals?.subj?.extra),
        '', // Bonus total (već u totalu)
        fmtMoney(totals?.obj?.total),   fmtMoney(totals?.subj?.total),
        totals?.obj?.paid_break_minutes ?? 0
      ]);

      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Teachers Work');
      XLSX.writeFile(wb, 'TeachersWorkReport.xlsx');
    } catch (e) {
      console.error('Excel export failed:', e);
      alert('Export failed');
    }
  };

  const handlePrint = () => {
    if (!filters.teacher_id) {
      alert('Please choose a teacher.');
      return;
    }
    if (!visits.length) {
      alert('No data to print for the selected period/teacher.');
      return;
    }
    window.print();
  };

  // --- Print CSS once ---
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        html, body, #teachers-report-print {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        body * { visibility: hidden !important; }
        #teachers-report-print, #teachers-report-print * { visibility: visible !important; }
        #teachers-report-print { position: absolute; left: 0; top: 0; width: 100vw; background: white; }
        #teachers-report-print thead { display: table-header-group; }
        #teachers-report-print tfoot { display: table-footer-group; }
        #teachers-report-print tr, #teachers-report-print img {
          break-inside: avoid; page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Prepravljene totalke u miles
  const totalObjMi  = kmToMiles(totals?.obj?.distance_km ?? 0) ?? 0;
  const totalSubjMi = kmToMiles(totals?.subj?.distance_km ?? 0) ?? 0;

  return (
    <div className="p-4 text-sm">
      <h2 className="text-lg font-bold mb-4 no-print">Teacher Work &amp; Cost Report</h2>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-4 no-print">
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          className="border p-1 rounded"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          className="border p-1 rounded"
        />
        <select
          value={filters.teacher_id}
          onChange={(e) => setFilters((f) => ({ ...f, teacher_id: e.target.value }))}
          className="border p-1 rounded"
        >
          <option value="">Choose teacher</option>
          {teachers.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-2 no-print">
        <button onClick={fetchReport} className="bg-blue-600 text-white px-4 py-2 rounded">
          Apply Filters
        </button>
        <button onClick={handleExportExcel} className="bg-green-500 text-white px-4 py-2 rounded">
          Export Excel
        </button>
        <button onClick={handlePrint} className="bg-gray-600 text-white px-4 py-2 rounded">
          Print
        </button>
      </div>

      {/* Report */}
      <div id="teachers-report-print" ref={reportRef} className="bg-white max-w-6xl mx-auto p-2">
        <div className="print-header mb-4 flex flex-col gap-1 border rounded p-2 bg-gray-50">
          <div className="flex items-center gap-3">
            <img
              src={brandLogoUrl || '/VisitOMS_logo.png'}
              alt={brandName || 'Brand logo'}
              className="w-14 h-14 object-contain"
            />
            <div>
              <div className="text-base font-semibold">Teacher Work Report</div>
              {brandName && (
                <div className="text-xs text-gray-600">
                  {brandName}
                </div>
              )}
              <div className="text-[10px] text-gray-400">
                Generated with VisitOMS EasyCare software
              </div>
            </div>
          </div>
          <div><strong>Teacher:</strong> {safe(teacherName, 'All')}</div>
          {teacherAddress ? <div><strong>Address:</strong> {teacherAddress}</div> : null}
          <div><strong>Period:</strong> {filters.date_from || '—'} – {filters.date_to || '—'}</div>
          <div><strong>Visits (excluding No-show):</strong> {nonNoShowCount}</div>
          <div><strong>Date of print:</strong> {(serverTimeStr || '').slice(0, 10) || new Date().toISOString().slice(0,10)}</div>
        </div>

        {/* TOP TOTALS */}
        <div className="mb-2 p-2 border rounded bg-gray-50 flex flex-wrap gap-4">
          <span><strong>Total hours (obj/subj):</strong> {toFixed(totals?.obj?.hours)} / {toFixed(totals?.subj?.hours)}</span>
          <span><strong>Total miles (obj/subj):</strong> {toFixed(totalObjMi)} / {toFixed(totalSubjMi)}</span>
          <span><strong>Total route time (obj/subj):</strong> {toFixed(totals?.obj?.route_minutes)} / {toFixed(totals?.subj?.route_minutes)} min</span>
          <span><strong>Total travel cost (obj/subj):</strong> ${fmtMoney(totals?.obj?.travel)} / ${fmtMoney(totals?.subj?.travel)}</span>
          <span><strong>Total cost (obj/subj, USD):</strong> ${fmtMoney(totals?.obj?.total)} / ${fmtMoney(totals?.subj?.total)}</span>
          <span className="text-blue-700">
            <strong>Paid breaks (obj/subj):</strong> {toFixed(totals?.obj?.paid_break_minutes)} / {toFixed(totals?.subj?.paid_break_minutes)} min
            {' '}(${fmtMoney(totals?.obj?.paid_break_cost)} / ${fmtMoney(totals?.subj?.paid_break_cost)})
          </span>
        </div>

        {loading ? (
          <div className="py-8 text-gray-500">Loading...</div>
        ) : loadError ? (
          <div className="p-4 text-red-600">{loadError}</div>
        ) : visits.length === 0 ? (
          <div className="p-4">No data for selected period.</div>
        ) : (
          dayKeys.map((date) => {
            const dayVisits = groupedVisits[date] || [];
            const subtotal = dailyTotals.find((dt) => dt.date === date);
            const dayBonus = dayVisits.reduce((s, r) => s + (Number(r?.bonus) || 0), 0);

            // Subtotal miles (konverzija iz km)
            const subObjMi  = kmToMiles(subtotal?.obj?.distance_km ?? 0) ?? 0;
            const subSubjMi = kmToMiles(subtotal?.subj?.distance_km ?? 0) ?? 0;

            const showPaidBreakPanel =
              !!subtotal &&
              (((subtotal?.obj?.paid_break_minutes ?? 0) > 0) ||
               ((subtotal?.subj?.paid_break_minutes ?? 0) > 0));

            return (
              <div key={date} className="mb-8 print:mb-2">
                <h3 className="font-semibold text-blue-700 mb-1">{date}</h3>

                {/* Dnevni rezime pauza — prikazi samo ako postoje */}
                {showPaidBreakPanel && (
                  <div className="mb-2 text-xs bg-blue-50 text-blue-800 border border-blue-100 rounded p-2 inline-flex flex-wrap gap-3">
                    <span><strong>Paid breaks:</strong> {subtotal?.paid_breaks_count || 0} × 10 min</span>
                    <span><strong>Obj:</strong> {toFixed(subtotal?.obj?.paid_break_minutes || 0)} min (${fmtMoney(subtotal?.obj?.paid_break_cost || 0)})</span>
                    <span><strong>Subj:</strong> {toFixed(subtotal?.subj?.paid_break_minutes || 0)} min (${fmtMoney(subtotal?.subj?.paid_break_cost || 0)})</span>
                  </div>
                )}

                <table className="w-full border border-gray-200 text-xs mb-2 print:text-[11px]">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-1 text-left">Student</th>
                      <th className="border p-1 text-left">Time</th>
                      <th className="border p-1 text-right">Hours</th>
                      <th className="border p-1 text-right">Distance</th>
                      <th className="border p-1 text-right">Route time (min)</th>
                      <th className="border p-1 text-right">Service ($)</th>
                      <th className="border p-1 text-right">Travel ($)</th>
                      <th className="border p-1 text-right">Extra ($)</th>
                      <th className="border p-1 text-right">Bonus ($)</th>
                      <th className="border p-1 text-right">Total ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayVisits.map((row) => {
                      const obj = row?.obj || {};
                      const subj = row?.subj || {};

                      // STRICT numeric check to avoid truthy issues like "0" string
                      const hasPaidBreak = Number(row?.paid_break_min) > 0;

                      // Stable key: prefer BE visit_id; fallback to composite
                      const stableKey =
                        row?.visit_id ??
                        `${row?.date || ''}__${row?.timeSlot || ''}__${row?.student || ''}`;

                      let rowClass = "align-top hover:bg-blue-50 print:bg-white";
                      if (row.isNoShow) {
                        rowClass += " bg-purple-50";
                      } else if (row.isPI) {
                        rowClass += " bg-indigo-50";
                      }

                      const objMi = kmToMiles(obj.distance_km);
                      const subjMi = kmToMiles(subj.distance_km);

                      return (
                        <tr key={stableKey} className={rowClass}>
                          {/* Student + badges */}
                          <td className="border p-1">
                            <div className="flex items-center gap-2">
                              <span>{safe(row?.student)}</span>
                              {hasPaidBreak && (
                                <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-[9px] font-semibold">
                                  Paid break +10m
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-0.5 text-[9px]">
                              {row.isPI && (
                                <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-semibold uppercase tracking-wide">
                                  PI
                                </span>
                              )}
                              {row.isNoShow && (
                                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                                  No-show (travel only)
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Time */}
                          <td className="border p-1">{safe(row?.timeSlot, '')}</td>

                          {/* Hours */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">{toFixed(obj.hours)}</div>
                            <div className="text-gray-500">{toFixed(subj.hours)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.service_formula_hours, '-') }<br />
                              Subj: {safe(subj.service_formula_hours, '-') }
                            </div>
                          </td>

                          {/* Distance */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">
                              {toFixed(obj.distance_km)} km {objMi != null ? `(${toFixed(objMi)} mi)` : ''}
                            </div>
                            <div className="text-gray-500">
                              {toFixed(subj.distance_km)} km {subjMi != null ? `(${toFixed(subjMi)} mi)` : ''}
                            </div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.travel_formula_distance, '-') }<br />
                              Subj: {safe(subj.travel_formula_distance, '-') }
                            </div>
                          </td>

                          {/* Route time (min) */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">{toFixed(obj.route_minutes)}</div>
                            <div className="text-gray-500">{toFixed(subj.route_minutes)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.travel_formula_time, '-') }<br />
                              Subj: {safe(subj.travel_formula_time, '-') }
                            </div>
                          </td>

                          {/* Service */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">${fmtMoney(obj.service_cost)}</div>
                            <div className="text-gray-500">${fmtMoney(subj.service_cost)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.service_formula_cost, '-') }<br />
                              Subj: {safe(subj.service_formula_cost, '-') }
                            </div>
                          </td>

                          {/* Travel */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">${fmtMoney(obj.travel_cost)}</div>
                            <div className="text-gray-500">${fmtMoney(subj.travel_cost)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.travel_formula_cost, '-') }<br />
                              Subj: {safe(subj.travel_formula_cost, '-') }
                            </div>
                          </td>

                          {/* Extra (subj) */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">-</div>
                            <div className="text-gray-500">${fmtMoney(subj.extra_cost)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Subj: {safe(subj.extra_formula, '-') }
                            </div>
                          </td>

                          {/* Bonus */}
                          <td className="border p-1 text-right">
                            <div className="font-medium">
                              ${fmtMoney(row?.bonus || 0)}
                            </div>
                          </td>

                          {/* Total */}
                          <td className="border p-1 text-right">
                            <div className="font-semibold">${fmtMoney(obj.total_cost)}</div>
                            <div className="text-gray-500">${fmtMoney(subj.total_cost)}</div>
                            <div className="text-[10px] text-gray-500 italic">
                              Obj: {safe(obj.total_formula, '-') }<br />
                              Subj: {safe(subj.total_formula, '-') }
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {subtotal && (
                      <tr className="font-bold bg-gray-100 print:bg-gray-100">
                        <td className="border p-1">Subtotal</td>
                        <td className="border p-1"></td>
                        <td className="border p-1 text-right">
                          {toFixed(subtotal?.obj?.hours)}
                          <span className="text-gray-500"> / {toFixed(subtotal?.subj?.hours)}</span>
                        </td>
                        {/* Distance in miles (obj/subj) */}
                        <td className="border p-1 text-right">
                          {toFixed(subObjMi)}<span className="text-gray-500"> / {toFixed(subSubjMi)}</span>
                        </td>
                        <td className="border p-1 text-right">
                          {toFixed(subtotal?.obj?.route_minutes)}
                          <span className="text-gray-500"> / {toFixed(subtotal?.subj?.route_minutes)}</span>
                        </td>
                        <td className="border p-1 text-right">
                          ${fmtMoney(subtotal?.obj?.service)}
                          <span className="text-gray-500"> / ${fmtMoney(subtotal?.subj?.service)}</span>
                        </td>
                        <td className="border p-1 text-right">
                          ${fmtMoney(subtotal?.obj?.travel)}
                          <span className="text-gray-500"> / ${fmtMoney(subtotal?.subj?.travel)}</span>
                        </td>
                        <td className="border p-1 text-right">
                          - <span className="text-gray-500"> / ${fmtMoney(subtotal?.subj?.extra)}</span>
                        </td>
                        <td className="border p-1 text-right">
                          ${fmtMoney(dayBonus)}
                        </td>
                        <td className="border p-1 text-right">
                          ${fmtMoney(subtotal?.obj?.total)}
                          <span className="text-gray-500"> / ${fmtMoney(subtotal?.subj?.total)}</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })
        )}

        {/* BOTTOM TOTALS (duplirani rezime zbog preglednosti) */}
        {visits.length > 0 && (
          <div className="mb-4 p-2 border rounded bg-gray-50 print:bg-gray-100">
            <span className="mr-4"><strong>Total hours (obj/subj):</strong> {toFixed(totals?.obj?.hours)} / {toFixed(totals?.subj?.hours)}</span>
            <span className="mr-4"><strong>Total miles (obj/subj):</strong> {toFixed(totalObjMi)} / {toFixed(totalSubjMi)}</span>
            <span className="mr-4"><strong>Total route time (obj/subj):</strong> {toFixed(totals?.obj?.route_minutes)} / {toFixed(totals?.subj?.route_minutes)} min</span>
            <span className="mr-4"><strong>Total travel cost (obj/subj):</strong> ${fmtMoney(totals?.obj?.travel)} / ${fmtMoney(totals?.subj?.travel)}</span>
            <span className="mr-4"><strong>Total cost (obj/subj, USD):</strong> ${fmtMoney(totals?.obj?.total)} / ${fmtMoney(totals?.subj?.total)}</span>
            <span className="text-blue-700 ml-4">
              <strong>Paid breaks (obj/subj):</strong> {toFixed(totals?.obj?.paid_break_minutes)} / {toFixed(totals?.subj?.paid_break_minutes)} min
              {' '}(${fmtMoney(totals?.obj?.paid_break_cost)} / ${fmtMoney(totals?.subj?.paid_break_cost)})
            </span>
          </div>
        )}
      </div>
    </div>
  );
}




